import { useState, useId, useMemo } from 'react'
import { Button, buttonClass } from './Button'
import { Dialog } from './Dialog'
import { Icon } from './Icon'
import { PRODUCT_NAME } from '../pages'
import {
  type Feedback,
  type FeedbackCategory,
  type FeedbackContext,
  buildFeedbackMarkdown,
  buildGitHubIssueUrl,
} from '../state/feedback'

export interface FeedbackDialogProps {
  isOpen: boolean
  onClose: () => void
  route: string
  reportCount: number
  softwareCount: number
  theme: string
  /** Names of the sources followed. */
  sources: string[]
}

const CATEGORIES: Array<{ id: FeedbackCategory; label: string; placeholder: string }> = [
  {
    id: 'bug',
    label: 'Bug or issue',
    placeholder:
      'What went wrong? Describe what happened, what you expected, and steps to reproduce...',
  },
  {
    id: 'feature',
    label: 'Feature suggestion',
    placeholder:
      'What would you like to see improved or added, and why would it help your workflow?',
  },
  {
    id: 'data',
    label: 'Indicator or data',
    placeholder: 'Which indicator, tool, or metric has an unexpected outcome or definition?',
  },
  {
    id: 'general',
    label: 'General feedback',
    placeholder: `Share your thoughts, suggestions, or comments about ${PRODUCT_NAME}...`,
  },
]

/**
 * Feedback, written here and sent by the person: it ends in a pre-filled
 * GitHub issue or a copy on the clipboard. Nothing is stored or sent.
 */
export function FeedbackDialog({
  isOpen,
  onClose,
  route,
  reportCount,
  softwareCount,
  theme,
  sources,
}: FeedbackDialogProps) {
  const [category, setCategory] = useState<FeedbackCategory>('bug')
  const [message, setMessage] = useState('')
  // Nothing about the session goes into the report unless asked for.
  const [includeContext, setIncludeContext] = useState(false)
  const [copied, setCopied] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const messageId = useId()
  const categoryName = useId()

  const context = useMemo<FeedbackContext>(
    () => ({
      route,
      reportCount,
      softwareCount,
      sources,
      theme,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      browser: navigator.userAgent,
      timestamp: new Date().toISOString(),
    }),
    [route, reportCount, softwareCount, theme, sources],
  )

  const feedback: Feedback = {
    category,
    message: message.trim(),
    context: includeContext ? context : undefined,
  }

  const handleClose = () => {
    setCategory('bug')
    setMessage('')
    setIncludeContext(false)
    setCopied(false)
    setValidationError(null)
    onClose()
  }

  /** False, with the reason shown, when there is nothing to send yet. */
  const ready = () => {
    if (feedback.message) return true
    setValidationError('Please provide details for your feedback first.')
    return false
  }

  const handleCopy = async () => {
    if (!ready()) return
    try {
      await navigator.clipboard.writeText(buildFeedbackMarkdown(feedback))
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      // The clipboard is not permitted here; the GitHub link still works.
    }
  }

  const placeholder = (CATEGORIES.find((c) => c.id === category) ?? CATEGORIES[0]!).placeholder

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      labelledBy="feedback-dialog-title"
      className="feedback-dialog"
    >
      <div className="feedback-dialog-header">
        <div className="feedback-dialog-title-group">
          <h2 id="feedback-dialog-title" className="feedback-dialog-title">
            Share your feedback
          </h2>
          <p className="feedback-dialog-subtitle">
            Help us improve {PRODUCT_NAME}. Nothing is sent from here: open it as a GitHub issue on
            the {PRODUCT_NAME} repository, or copy it.
          </p>
        </div>
        <Button
          tone="quiet"
          size="small"
          icon="close"
          iconOnly
          label="Close feedback dialog"
          className="feedback-dialog-close"
          onClick={handleClose}
        />
      </div>

      <div className="feedback-dialog-body">
        <form className="feedback-form" onSubmit={(e) => e.preventDefault()}>
          <fieldset className="feedback-field-group">
            <legend className="feedback-label">What kind of feedback do you have?</legend>
            <div className="feedback-category-pills">
              {CATEGORIES.map((cat) => (
                <label
                  key={cat.id}
                  className={`feedback-category-pill${category === cat.id ? ' is-active' : ''}`}
                >
                  <input
                    type="radio"
                    name={categoryName}
                    className="visually-hidden"
                    checked={category === cat.id}
                    onChange={() => setCategory(cat.id)}
                  />
                  <span>{cat.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="feedback-field-group">
            <label htmlFor={messageId} className="feedback-label">
              Details <span className="feedback-required">*</span>
            </label>
            <textarea
              id={messageId}
              className={`feedback-textarea${validationError ? ' is-invalid' : ''}`}
              rows={5}
              placeholder={placeholder}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value)
                if (validationError) setValidationError(null)
              }}
              required
            />
            <p className="feedback-note">The first line becomes the issue title.</p>
            {validationError && (
              <p className="feedback-error-text" role="alert">
                <Icon name="warn" size={14} />
                <span>{validationError}</span>
              </p>
            )}
          </div>

          <div className="feedback-context-section">
            <label className="feedback-checkbox-label">
              <input
                type="checkbox"
                checked={includeContext}
                onChange={(e) => setIncludeContext(e.target.checked)}
              />
              <span>
                Include diagnostic context: the page, how many reports are loaded, the sources you
                follow, your screen size and browser
              </span>
            </label>
            {includeContext && (
              <details className="feedback-context-details">
                <summary>Preview what is added</summary>
                <pre className="feedback-context-preview">{JSON.stringify(context, null, 2)}</pre>
              </details>
            )}
          </div>
        </form>
      </div>

      <div className="feedback-dialog-footer">
        <Button tone="quiet" size="small" label="Cancel" onClick={handleClose} />
        <Button
          size="small"
          icon={copied ? 'check' : 'copy'}
          label={copied ? 'Copied to the clipboard' : 'Copy'}
          onClick={() => void handleCopy()}
        />
        <a
          href={buildGitHubIssueUrl(feedback)}
          target="_blank"
          rel="noreferrer"
          className={buttonClass({ tone: 'primary', size: 'small' })}
          onClick={(e) => {
            if (!ready()) e.preventDefault()
          }}
        >
          <Icon name="link" size={14} />
          <span>Open as GitHub issue</span>
        </a>
      </div>
    </Dialog>
  )
}
